import type { Vec3 } from './types';
import { dacos, dasin, datan2, dsin } from './core/dmath';

// =============================================================================
// Pure vector math — no Three.js dependency, usable in tests and headless sim
// =============================================================================

export function vec3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function scale(v: Vec3, s: number): Vec3 {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

export function addScaled(a: Vec3, b: Vec3, s: number): Vec3 {
  return { x: a.x + b.x * s, y: a.y + b.y * s, z: a.z + b.z * s };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function lengthSq(v: Vec3): number {
  return v.x * v.x + v.y * v.y + v.z * v.z;
}

export function length(v: Vec3): number {
  return Math.sqrt(lengthSq(v));
}

export function normalize(v: Vec3): Vec3 {
  const len = length(v);
  if (len < 1e-10) return { x: 0, y: 0, z: 0 };
  return scale(v, 1 / len);
}

export function distance(a: Vec3, b: Vec3): number {
  return length(sub(a, b));
}

export function distanceSq(a: Vec3, b: Vec3): number {
  return lengthSq(sub(a, b));
}

export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

export function clone(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z };
}

/**
 * Spherical linear interpolation between two unit vectors.
 * t=0 returns a, t=1 returns b. Result is always normalized.
 */
export function slerp(a: Vec3, b: Vec3, t: number): Vec3 {
  let d = dot(a, b);
  d = Math.max(-1, Math.min(1, d));

  // If vectors are nearly identical, fall back to normalized lerp
  if (d > 0.9995) {
    const result = lerp(a, b, t);
    return normalize(result);
  }

  const theta = dacos(d);
  const sinTheta = dsin(theta);
  const wa = dsin((1 - t) * theta) / sinTheta;
  const wb = dsin(t * theta) / sinTheta;

  return {
    x: a.x * wa + b.x * wb,
    y: a.y * wa + b.y * wb,
    z: a.z * wa + b.z * wb,
  };
}

// Aliases for convenience
export const vecAdd = add;
export const vecSub = sub;
export const vecScale = scale;
export const vecDot = dot;
export const vecCross = cross;
export const vecLength = length;
export const vecLengthSq = lengthSq;
export const vecNormalize = normalize;
export const vecDistance = distance;
export const vecLerp = lerp;
export const vecSlerp = slerp;
export const vecClone = clone;

// =============================================================================
// Surface tangent frame — the ONE definition of "azimuth 0" on a planet surface.
// Used by launch direction, model facing, aim indicator, and bot aim inversion.
// Mirrored in wasm-physics/src/lib.rs (compute_launch_direction) — keep in sync.
// =============================================================================

export interface TangentFrame {
  tx: Vec3; // azimuth = 0 direction (roughly "east" relative to world +Y)
  ty: Vec3; // azimuth = +90°
}

/**
 * Build an orthonormal tangent frame for a surface normal. The reference is
 * world +Y, so the frame is continuous everywhere except a ~2.5° cap at the
 * world poles (|n.y| > 0.999), where it falls back to +X.
 */
export function surfaceTangentFrame(normal: Vec3): TangentFrame {
  const n = normalize(normal);
  let ref: Vec3 = { x: 0, y: 1, z: 0 };
  if (Math.abs(n.y) > 0.999) ref = { x: 1, y: 0, z: 0 };
  const tx = normalize(cross(n, ref));
  const ty = normalize(cross(n, tx));
  return { tx, ty };
}

/**
 * The frame a model stood upright on the ground is drawn in: the shortest rotation taking +y to `normal` — three's
 * `Quaternion.setFromUnitVectors(+y, normal)`, as the renderer seats every structure — its x and z (right-handed: x × up
 * = z, as the model's own axes). Not `surfaceTangentFrame`, whose (tx, normal, ty) is turned by a yaw that depends on the
 * place and mirrored against the drawn model. Plain arithmetic (the sim reads it); straight down it turns half round z,
 * as three does.
 */
export function uprightFrame(normal: Vec3): { tx: Vec3; tz: Vec3 } {
  const n = normalize(normal);
  const c = 1 + n.y;
  if (c < Number.EPSILON) return { tx: { x: -1, y: 0, z: 0 }, tz: { x: 0, y: 0, z: 1 } };
  return {
    tx: { x: 1 - (n.x * n.x) / c, y: -n.x, z: -(n.x * n.z) / c },
    tz: { x: -(n.x * n.z) / c, y: -n.z, z: 1 - (n.z * n.z) / c },
  };
}

/**
 * Invert a world direction into (azimuth, elevation) in the surface frame.
 * Exact inverse of computeLaunchDirection for unit directions.
 */
export function directionToAzimuthElevation(normal: Vec3, dir: Vec3): { azimuth: number; elevation: number } {
  const n = normalize(normal);
  const d = normalize(dir);
  const { tx, ty } = surfaceTangentFrame(n);
  const up = dot(d, n);
  const tangent = { x: d.x - up * n.x, y: d.y - up * n.y, z: d.z - up * n.z };
  const azimuth = lengthSq(tangent) > 1e-12 ? datan2(dot(tangent, ty), dot(tangent, tx)) : 0;
  const elevation = dasin(Math.max(-1, Math.min(1, up)));
  return { azimuth, elevation };
}
